import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { coachApi, orderApi, profileApi, type CouponItem, type CustomerAdItem } from "@/api";
import { auth } from "@/lib/auth";
import { resolveCustomerAssetUrl } from "@/lib/asset";
import { locationStore } from "@/lib/location";
import BottomNav from "@/components/feature/BottomNav";
import { therapistList } from "@/mocks/massage";
import type { CoachListItem } from "@/types/api";

interface Therapist {
  id: number;
  name: string;
  level: string;
  levelStyle: { color: string; bg: string; border?: string };
  status: string;
  statusStyle: { color: string; bg: string };
  rating: number;
  orders: number;
  earliestTime: string;
  distance: string;
  favorited: boolean;
  favorites: number;
  specialties: string[];
  intro: string;
  location: string;
  age: string;
  years: string;
  tags: string[];
  avatar: string;
  video: string | null;
  lifePhotos: string[];
  photos: string[];
}

interface ProfileCoupon {
  id: number;
  title: string;
  amount: string;
  unit: "元";
  expiry: string;
  condition: string;
  urgent: boolean;
}

interface ProfileAd {
  id: number;
  tag: string;
  title: string;
  subtitle: string;
  cta: string;
  bg: string;
  accent: string;
  image: string;
  linkType: string;
  linkValue: string;
}

const SERVED_THERAPIST_IDS = [1, 3];

const fallbackServedTherapists: Therapist[] = therapistList.filter((t) =>
  SERVED_THERAPIST_IDS.includes(t.id)
);
const fallbackRecommendedTherapists: Therapist[] = therapistList.filter(
  (t) => !SERVED_THERAPIST_IDS.includes(t.id)
);

const AVAILABLE_STATUS_TEXT = "可服务";
const BUSY_STATUS_TEXT = "服务中";
const PAUSED_STATUS_TEXT = "暂停服务";
const DEFAULT_LEVEL_STYLE = { color: "#8B6FE8", bg: "#F2EEFF", border: "#C9BCFF" };
const NEWBIE_LEVEL_STYLE = { color: "#FF7B22", bg: "#FFF3EC", border: "#FFD4B0" };
const SENIOR_LEVEL_STYLE = { color: "#C8860A", bg: "#FFF8E7", border: "#FFD980" };
const AVAILABLE_STATUS_STYLE = { color: "#17B978", bg: "#E8FBF3" };
const BUSY_STATUS_STYLE = { color: "#FF7B22", bg: "#FFF3EC" };
const PAUSED_STATUS_STYLE = { color: "#909399", bg: "#F4F4F5" };
const DEFAULT_THERAPIST_AVATAR =
  "https://readdy.ai/api/search-image?query=professional%20massage%20therapist%20portrait&width=160&height=160&seq=profile-therapist-avatar&orientation=squarish";

function getLevelStyle(level?: string) {
  if (level === "新人") {
    return NEWBIE_LEVEL_STYLE;
  }
  if (level === "高级" || level === "资深" || level === "首席") {
    return SENIOR_LEVEL_STYLE;
  }
  return DEFAULT_LEVEL_STYLE;
}

function getStatusInfo(serverStatus?: number, workStatus?: number) {
  if (typeof serverStatus === "number" && serverStatus !== 1) {
    return { text: PAUSED_STATUS_TEXT, style: PAUSED_STATUS_STYLE };
  }
  if (Number(workStatus || 0) === 1) {
    return { text: AVAILABLE_STATUS_TEXT, style: AVAILABLE_STATUS_STYLE };
  }
  return { text: BUSY_STATUS_TEXT, style: BUSY_STATUS_STYLE };
}

function getCoachPhotos(item: {
  workPhoto?: string;
  work_photo?: string;
  photos?: string[];
  life_photo?: string[];
}) {
  const sources = [
    ...(Array.isArray(item.photos) ? item.photos : []),
    ...(Array.isArray(item.life_photo) ? item.life_photo : []),
    item.workPhoto || "",
    item.work_photo || "",
  ];
  return sources
    .map((photo) => resolveCustomerAssetUrl(String(photo || "")))
    .filter(Boolean) as string[];
}

function mapCoachListItemToTherapist(item: CoachListItem): Therapist {
  const level = String(item.level || "平台技师");
  const status = getStatusInfo(item.serverStatus, item.workStatus);
  const photos = getCoachPhotos(item);
  const avatar =
    resolveCustomerAssetUrl(String(item.workPhoto || photos[0] || "")) ||
    DEFAULT_THERAPIST_AVATAR;

  return {
    id: item.id,
    name: String(item.name || "技师"),
    level,
    levelStyle: getLevelStyle(level),
    status: status.text,
    statusStyle: status.style,
    rating: Number(item.rating || 0),
    orders: Number(item.orderNum || 0),
    earliestTime: String(item.earliestTime || item.first_appoint || "尽快上门"),
    distance: String(item.distanceDesc || ""),
    favorited: Number(item.favorited || 0) === 1,
    favorites: Number(item.favorites || 0),
    specialties: Array.isArray(item.specialties) && item.specialties.length ? item.specialties : ["上门服务"],
    intro: String(item.serviceSummary || "平台认证技师"),
    location: String(item.address || ""),
    age: item.age ? `${item.age}岁` : "平台认证",
    years: item.age ? `${item.age}岁` : "平台认证",
    tags: Array.isArray(item.operationTags) ? item.operationTags : [],
    avatar,
    video: item.introVideo || null,
    lifePhotos: photos,
    photos,
  };
}

function mapCoachDetailToTherapist(
  detail: Awaited<ReturnType<typeof coachApi.getCoachDetail>>,
  fallback?: { id: number; name?: string; avatar?: string },
): Therapist {
  const level = String(detail.level || "平台技师");
  const status = getStatusInfo(undefined, detail.work_status);
  const photos = getCoachPhotos(detail);
  const avatar =
    resolveCustomerAssetUrl(String(detail.work_photo || fallback?.avatar || photos[0] || "")) ||
    DEFAULT_THERAPIST_AVATAR;

  return {
    id: Number(detail.id || fallback?.id || 0),
    name: String(detail.name || fallback?.name || "技师"),
    level,
    levelStyle: getLevelStyle(level),
    status: status.text,
    statusStyle: status.style,
    rating: Number(detail.display_rating || detail.score_comment || 0),
    orders: Number(detail.display_order_num || detail.order_num || 0),
    earliestTime: String(detail.first_appoint || "尽快上门"),
    distance: String(detail.distance_desc || ""),
    favorited: Number(detail.is_collect || 0) === 1,
    favorites: Number(detail.collect_num || 0),
    specialties: Array.isArray(detail.specialties) && detail.specialties.length ? detail.specialties : ["上门服务"],
    intro: String(detail.introduction || "平台认证技师"),
    location: String(detail.shop_name || detail.shop?.address_detail || ""),
    age: detail.age ? `${detail.age}岁` : "平台认证",
    years: detail.age ? `${detail.age}岁` : "平台认证",
    tags: Array.isArray(detail.operation_tags) ? detail.operation_tags : [],
    avatar,
    video: detail.intro_video || null,
    lifePhotos: photos,
    photos,
  };
}

function formatWalletMoney(value?: number | string | null) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
}

function toCount(value?: number | string | null) {
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
}

function maskMobile(value?: string) {
  const mobile = String(value || "").trim();
  if (mobile.length < 7) {
    return mobile;
  }
  return `${mobile.slice(0, 3)}****${mobile.slice(-4)}`;
}

function buildProfileLocationText(addressList?: Awaited<ReturnType<typeof profileApi.getAddressList>>, mobile?: string) {
  const usableAddresses = Array.isArray(addressList?.usable) ? addressList.usable : [];
  const targetAddress =
    usableAddresses.find((item) => Number(item.is_default || 0) === 1) ||
    usableAddresses[0];
  const locationText = [targetAddress?.province, targetAddress?.city, targetAddress?.district]
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join("");

  return locationText || maskMobile(mobile) || "暂未设置地址";
}

function normalizeCouponDateText(value?: string) {
  if (!value) {
    return "长期有效";
  }
  return value.replace("T", " ").slice(0, 10);
}

function calculateCouponDaysLeft(value?: string) {
  if (!value) {
    return null;
  }
  const target = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(target.getTime())) {
    return null;
  }
  return Math.ceil((target.getTime() - Date.now()) / 86400000);
}

function mapProfileCoupon(item: CouponItem): ProfileCoupon {
  const daysLeft = calculateCouponDaysLeft(item.use_end_time_text);
  return {
    id: item.id,
    title: String(item.tips || "平台优惠券"),
    amount: String(item.money || "0"),
    unit: "元",
    expiry: normalizeCouponDateText(item.use_end_time_text),
    condition: String(item.condition || "无门槛"),
    urgent: daysLeft !== null && daysLeft >= 0 && daysLeft <= 7,
  };
}

function formatCouponStripText(coupon: ProfileCoupon) {
  const condition = coupon.condition.trim();
  const conditionText =
    !condition || condition === "无门槛"
      ? "无门槛"
      : condition.replace("可用", "");
  const expiryText = coupon.urgent ? " · 即将到期" : "";
  return `${conditionText}可抵 ¥${coupon.amount}${expiryText}`;
}

function mapProfileAd(item: CustomerAdItem): ProfileAd | null {
  const title = String(item.title || "").trim();
  const image = resolveCustomerAssetUrl(String(item.image_url || ""));
  if (!title || !image) {
    return null;
  }
  return {
    id: Number(item.id || 0),
    tag: String(item.tag_text || "").trim(),
    title,
    subtitle: String(item.subtitle || "").trim(),
    cta: String(item.button_text || "查看").trim() || "查看",
    bg: String(item.background_color || "#2C3E50").trim(),
    accent: String(item.accent_color || "#FF7B22").trim(),
    image,
    linkType: String(item.link_type || "NONE").trim().toUpperCase(),
    linkValue: String(item.link_value || "").trim(),
  };
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState("功能建议");
  const [feedbackContent, setFeedbackContent] = useState("");
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const [addressSheetOpen, setAddressSheetOpen] = useState(false);
  const [couponsOpen, setCouponsOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [summaryCounts, setSummaryCounts] = useState({
    totalOrders: 3,
    couponCount: 0,
    followCount: 5,
    waitPayCount: 0,
    serviceCount: 1,
    waitCommentCount: 2,
  });
  const [servedTherapists, setServedTherapists] = useState<Therapist[]>(fallbackServedTherapists);
  const [recommendedTherapists, setRecommendedTherapists] = useState<Therapist[]>(fallbackRecommendedTherapists);
  const [profileCoupons, setProfileCoupons] = useState<ProfileCoupon[]>([]);
  const [ads, setAds] = useState<ProfileAd[]>([]);

  const urgentCoupon = profileCoupons.find((c) => c.urgent) || profileCoupons[0] || null;
  const [walletBalance, setWalletBalance] = useState("0.00");
  const [adIndex, setAdIndex] = useState(0);
  const adTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const userInfo = auth.getUserInfo();
  const [profileHeader, setProfileHeader] = useState(() => ({
    name: userInfo?.nickname?.trim() || "用户",
    avatar:
      resolveCustomerAssetUrl(String(userInfo?.avatar || "")) ||
      "https://readdy.ai/api/search-image?query=young%20stylish%20asian%20woman%20natural%20warm%20smile%20portrait%20minimal%20clean%20white%20background%20soft%20studio%20lighting%20fresh%20appearance&width=160&height=160&seq=profile_avatar_2026&orientation=squarish",
    location: maskMobile(userInfo?.mobile) || "暂未设置地址",
  }));

  useEffect(() => {
    if (ads.length <= 1) {
      setAdIndex(0);
      return;
    }
    adTimer.current = setInterval(() => {
      setAdIndex((prev) => (prev + 1) % ads.length);
    }, 3500);
    return () => {
      if (adTimer.current) clearInterval(adTimer.current);
    };
  }, [ads.length]);

  useEffect(() => {
    let cancelled = false;

    const loadProfileAds = async () => {
      try {
        const result = await profileApi.getAdPosition("PROFILE_HOME_BANNER");
        if (cancelled) {
          return;
        }
        const mappedAds = (result.items || [])
          .map(mapProfileAd)
          .filter((item): item is ProfileAd => item !== null);
        setAds(mappedAds);
      } catch {
        if (!cancelled) {
          setAds([]);
        }
      }
    };

    void loadProfileAds();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleAdClick = (ad: ProfileAd) => {
    if (ad.linkType === "PAGE" && ad.linkValue) {
      navigate(ad.linkValue.startsWith("/") ? ad.linkValue : `/${ad.linkValue}`);
      return;
    }
    if (ad.linkType === "URL" && ad.linkValue) {
      window.location.href = ad.linkValue;
      return;
    }
    if (ad.linkType === "GOODS" && ad.linkValue) {
      navigate(`/booking?goodsId=${encodeURIComponent(ad.linkValue)}`);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadProfileSummary = async () => {
      if (!auth.getToken()) {
        return;
      }
      try {
        const [walletResult, centerResult, userInfoResult, addressResult] = await Promise.all([
          profileApi.getWallet(),
          profileApi.getUserCenter(),
          profileApi.getUserInfo(),
          profileApi.getAddressList(),
        ]);
        const totalOrders =
          toCount(centerResult.waitpay_order_num) +
          toCount(centerResult.appoint_order_num) +
          toCount(centerResult.server_order_num) +
          toCount(centerResult.complete_order_num);
        if (!cancelled) {
          auth.setUserInfo({
            id: userInfoResult.id || centerResult.user_id || centerResult.id,
            nickname: userInfoResult.nickname || centerResult.nickname || userInfo?.nickname,
            avatar: userInfoResult.avatar || centerResult.avatar || userInfo?.avatar,
            mobile: userInfoResult.mobile || centerResult.mobile || userInfo?.mobile,
          });
          setWalletBalance(formatWalletMoney(walletResult.user_money ?? centerResult.user_money));
          setProfileHeader({
            name: String(userInfoResult.nickname || centerResult.nickname || userInfo?.nickname || "用户").trim() || "用户",
            avatar:
              resolveCustomerAssetUrl(String(userInfoResult.avatar || centerResult.avatar || userInfo?.avatar || "")) ||
              "https://readdy.ai/api/search-image?query=young%20stylish%20asian%20woman%20natural%20warm%20smile%20portrait%20minimal%20clean%20white%20background%20soft%20studio%20lighting%20fresh%20appearance&width=160&height=160&seq=profile_avatar_2026&orientation=squarish",
            location: buildProfileLocationText(addressResult, userInfoResult.mobile || centerResult.mobile || userInfo?.mobile),
          });
          setSummaryCounts({
            totalOrders,
            couponCount: toCount(centerResult.coupon_num),
            followCount: toCount(centerResult.collect_coach_num),
            waitPayCount: toCount(centerResult.waitpay_order_num),
            serviceCount:
              toCount(centerResult.appoint_order_num) +
              toCount(centerResult.server_order_num),
            waitCommentCount: toCount(centerResult.wait_comment_num),
          });
        }
      } catch {
        if (!cancelled) {
          setWalletBalance("0.00");
          setProfileHeader((current) => ({
            ...current,
            location: current.location || maskMobile(userInfo?.mobile) || "暂未设置地址",
          }));
        }
      }
    };

    void loadProfileSummary();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadProfileCoupons = async () => {
      if (!auth.getToken()) {
        setProfileCoupons([]);
        return;
      }
      try {
        const result = await profileApi.getCouponList({ type: 0, page_no: 1, page_size: 100 });
        if (cancelled) {
          return;
        }
        const availableCoupons = (result.lists || [])
          .filter((item) => Number(item.status || 0) === 0)
          .map(mapProfileCoupon)
          .sort((left, right) => {
            if (left.urgent !== right.urgent) {
              return left.urgent ? -1 : 1;
            }
            return left.expiry.localeCompare(right.expiry);
          });
        setProfileCoupons(availableCoupons);
        setSummaryCounts((current) => ({
          ...current,
          couponCount: Number(result.count || availableCoupons.length),
        }));
      } catch {
        if (!cancelled) {
          setProfileCoupons([]);
        }
      }
    };

    void loadProfileCoupons();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadTherapists = async () => {
      try {
        const currentLocation = locationStore.getFresh();
        const coachListResult = await coachApi.getCoachList({
          pageNum: 1,
          pageSize: 12,
          longitude: currentLocation?.longitude,
          latitude: currentLocation?.latitude,
          sortBy: "distance_asc",
        });
        const coachCards = (coachListResult.records || []).map(mapCoachListItemToTherapist);

        let servedCards: Therapist[] = [];
        const servedIds = new Set<number>();

        if (auth.getToken()) {
          try {
            const orderResult = await orderApi.getOrderList({
              page_no: 1,
              page_size: 20,
            });
            const servedCoaches = (orderResult.lists || [])
              .map((item) => ({
                id: Number(item.coach_info?.id || 0),
                name: item.coach_info?.name,
                avatar: item.coach_info?.work_photo,
              }))
              .filter((item) => item.id > 0)
              .filter((item, index, list) => list.findIndex((value) => value.id === item.id) === index)
              .slice(0, 4);

            const detailResults = await Promise.allSettled(
              servedCoaches.map((item) =>
                coachApi.getCoachDetail(item.id, {
                  longitude: currentLocation?.longitude,
                  latitude: currentLocation?.latitude,
                }),
              ),
            );

            servedCards = servedCoaches.map((item, index) => {
              const detailResult = detailResults[index];
              if (detailResult.status === "fulfilled") {
                servedIds.add(item.id);
                return mapCoachDetailToTherapist(detailResult.value, item);
              }

              const fallbackCoach = coachCards.find((coach) => coach.id === item.id);
              if (fallbackCoach) {
                servedIds.add(item.id);
                return fallbackCoach;
              }

              return {
                ...fallbackServedTherapists[0],
                id: item.id,
                name: item.name || fallbackServedTherapists[0].name,
                avatar: resolveCustomerAssetUrl(String(item.avatar || "")) || fallbackServedTherapists[0].avatar,
              };
            });
          } catch {
            servedCards = [];
          }
        }

        const recommendedCards = coachCards
          .filter((item) => !servedIds.has(item.id))
          .slice(0, 6);

        if (!cancelled) {
          setServedTherapists(servedCards);
          if (recommendedCards.length) {
            setRecommendedTherapists(recommendedCards);
          }
        }
      } catch {
        if (!cancelled) {
          setServedTherapists(auth.getToken() ? [] : fallbackServedTherapists);
          setRecommendedTherapists(fallbackRecommendedTherapists);
        }
      }
    };

    void loadTherapists();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleFeedbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!feedbackContent.trim()) {
      setFeedbackError("请填写反馈内容");
      return;
    }
    if (feedbackContent.length > 500) {
      setFeedbackError("内容不能超过500字");
      return;
    }
    setFeedbackError("");
    const params = new URLSearchParams();
    params.append("feedback_type", feedbackType);
    params.append("content", feedbackContent);
    if (feedbackEmail) params.append("email", feedbackEmail);
    try {
      await fetch("https://readdy.ai/api/form/d7ffgjia509fjl5l0bug", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      setFeedbackSubmitted(true);
    } catch {
      setFeedbackError("提交失败，请稍后重试");
    }
  };

  return (
    <div
      className="flex flex-col min-h-screen pb-24"
      style={{ background: "#F5F4F1", maxWidth: 480, margin: "0 auto" }}
    >
      {/* ── Hero Header ── */}
      <div
        className="sticky top-0 z-40 overflow-hidden pt-12 pb-5 px-4"
        style={{
          background: "linear-gradient(150deg, #FFB07A 0%, #FF8040 55%, #FF6018 100%)",
        }}
      >
        {/* decorative circles */}
        <div
          className="absolute -top-10 -right-10 w-48 h-48 rounded-full"
          style={{ background: "rgba(255,255,255,0.06)" }}
        />
        <div
          className="absolute top-8 right-16 w-20 h-20 rounded-full"
          style={{ background: "rgba(255,255,255,0.05)" }}
        />
        <div
          className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full"
          style={{ background: "rgba(255,255,255,0.04)" }}
        />

        {/* top bar */}
        <div className="flex items-center justify-between mb-5 relative z-10">
          <div className="flex items-center gap-3">
            {/* avatar */}
            <div
              className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0"
              style={{ border: "2.5px solid rgba(255,255,255,0.6)" }}
            >
              <img
                src={profileHeader.avatar}
                alt="头像"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-base font-bold text-white leading-tight">{profileHeader.name}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <i className="ri-map-pin-2-line text-[11px] text-white opacity-60" />
                <span className="text-[11px]" style={{ color: "rgba(255,255,255,0.65)" }}>
                  {profileHeader.location}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/service")}
              className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
              style={{ background: "rgba(255,255,255,0.18)" }}
            >
              <i className="ri-customer-service-2-line text-sm text-white" />
            </button>
            <button
              onClick={() => navigate("/profile-edit")}
              className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
              style={{ background: "rgba(255,255,255,0.18)" }}
            >
              <i className="ri-settings-3-line text-sm text-white" />
            </button>
          </div>
        </div>

        {/* 4-stat row */}
        <div
          className="grid grid-cols-4 gap-1 relative z-10 rounded-2xl py-3"
          style={{ background: "rgba(255,255,255,0.12)" }}
        >
          {[
            { label: "累计订单", value: String(summaryCounts.totalOrders), action: () => navigate("/orders") },
            { label: "优惠券", value: `${summaryCounts.couponCount}张`, action: () => navigate("/coupons") },
            { label: "钱包", value: `¥${walletBalance.split(".")[0]}`, action: () => navigate("/wallet") },
            { label: "关注技师", value: String(summaryCounts.followCount), action: () => navigate("/followed-therapists") },
          ].map((s, i) => (
            <button
              key={s.label}
              onClick={s.action}
              className={`flex flex-col items-center py-1 cursor-pointer ${
                i < 3 ? "border-r" : ""
              }`}
              style={{ borderColor: "rgba(255,255,255,0.2)" }}
            >
              <span className="text-sm font-bold text-white leading-none">{s.value}</span>
              <span className="text-[10px] mt-1" style={{ color: "rgba(255,255,255,0.65)" }}>
                {s.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Urgent Coupon Strip ── */}
      {urgentCoupon && (
        <button
          onClick={() => navigate("/coupons")}
          className="mx-3 mt-3 flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer active:opacity-80"
          style={{
            background: "#FFF4EC",
            border: "1px solid #FFD4A8",
          }}
        >
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "linear-gradient(135deg, #FF6018, #FF9A3C)" }}
          >
            <i className="ri-coupon-3-fill text-base text-white" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-xs font-semibold" style={{ color: "#1A1A1A" }}>
              {urgentCoupon.title}
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: "#FF6018" }}>
              {formatCouponStripText(urgentCoupon)}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <span
              className="text-[10px] px-2 py-1 rounded-full font-semibold whitespace-nowrap"
              style={{ background: "#FF6018", color: "#fff" }}
            >
              去使用
            </span>
            <i className="ri-arrow-right-s-line text-sm" style={{ color: "#FF9A3C" }} />
          </div>
        </button>
      )}

      {/* ── My Orders Quick Nav ── */}
      <div
        className="mx-3 mt-3 rounded-2xl overflow-hidden"
        style={{ background: "#fff", border: "1px solid #F0EDE8" }}
      >
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <span className="text-sm font-bold" style={{ color: "#1A1A1A" }}>
            我的订单
          </span>
          <button
            onClick={() => navigate("/orders")}
            className="flex items-center gap-0.5 cursor-pointer"
          >
            <span className="text-xs" style={{ color: "#FF6018" }}>全部订单</span>
            <i className="ri-arrow-right-s-line text-sm" style={{ color: "#FF9A3C" }} />
          </button>
        </div>
        <div className="grid grid-cols-4 pb-4">
          {[
            { icon: "ri-bill-line", label: "待付款", count: summaryCounts.waitPayCount },
            { icon: "ri-service-line", label: "进行中", count: summaryCounts.serviceCount },
            { icon: "ri-chat-smile-2-line", label: "待评价", count: summaryCounts.waitCommentCount },
            { icon: "ri-close-circle-line", label: "已取消", count: 0 },
          ].map((item) => (
            <button
              key={item.label}
              onClick={() => navigate("/orders")}
              className="flex flex-col items-center gap-1.5 py-2 cursor-pointer active:opacity-70"
            >
              <div className="relative w-10 h-10 flex items-center justify-center">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: "#FFF4EC" }}
                >
                  <i className={`${item.icon} text-lg`} style={{ color: "#FF6018" }} />
                </div>
                {item.count > 0 && (
                  <span
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold text-white flex items-center justify-center"
                    style={{ background: "#FF3B30" }}
                  >
                    {item.count}
                  </span>
                )}
              </div>
              <span className="text-[11px]" style={{ color: "#666" }}>
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Quick Feature Grid ── */}
      <div
        className="mx-3 mt-3 rounded-2xl overflow-hidden"
        style={{ background: "#fff", border: "1px solid #F0EDE8" }}
      >
        <div className="grid grid-cols-4">
          {[
            {
              icon: "ri-map-pin-2-line",
              label: "地址管理",
              color: "#FF6018",
              bg: "#FFF4EC",
              action: () => navigate("/address"),
            },
            {
              icon: "ri-coupon-3-line",
              label: "我的优惠券",
              color: "#E8860A",
              bg: "#FFF8E7",
              action: () => navigate("/coupons"),
            },
            {
              icon: "ri-wallet-3-line",
              label: "我的钱包",
              color: "#17A87B",
              bg: "#EDFBF4",
              action: () => navigate("/wallet"),
            },
            {
              icon: "ri-message-3-line",
              label: "建议反馈",
              color: "#9B72CF",
              bg: "#F3EEFF",
              action: () => navigate("/feedback"),
            },
          ].map((item, i) => (
            <button
              key={item.label}
              onClick={item.action}
              className={`flex flex-col items-center py-4 gap-2 cursor-pointer active:opacity-70 ${
                i < 3 ? "border-r" : ""
              }`}
              style={{ borderColor: "#F5F2EE" }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ background: item.bg }}
              >
                <i className={`${item.icon} text-xl`} style={{ color: item.color }} />
              </div>
              <span className="text-[11px] leading-tight text-center" style={{ color: "#555" }}>
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Ad Banner Carousel ── */}
      {ads.length > 0 && (
      <div className="mx-3 mt-3">
        <div
          className="relative overflow-hidden rounded-2xl"
          style={{ height: 100 }}
        >
          {ads.map((ad, i) => (
            <div
              key={ad.id}
              role="button"
              tabIndex={i === adIndex ? 0 : -1}
              onClick={() => handleAdClick(ad)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleAdClick(ad);
                }
              }}
              className="absolute inset-0 flex items-center transition-opacity duration-500 cursor-pointer active:opacity-90"
              style={{
                background: ad.bg,
                opacity: i === adIndex ? 1 : 0,
                pointerEvents: i === adIndex ? "auto" : "none",
              }}
            >
              {/* right image faded */}
              <div className="absolute right-0 top-0 bottom-0 w-40 overflow-hidden">
                <img
                  src={ad.image}
                  alt={ad.title}
                  className="w-full h-full object-cover object-top"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: "linear-gradient(90deg, rgba(0,0,0,0.75) 0%, transparent 70%)",
                  }}
                />
              </div>

              {/* left text */}
              <div className="relative z-10 px-4">
                <span
                  className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                  style={{ background: ad.accent, color: "#fff" }}
                >
                  {ad.tag}
                </span>
                <p className="text-sm font-bold text-white mt-1.5 leading-tight">{ad.title}</p>
                <p className="text-[11px] mt-0.5" style={{ color: "rgba(255,255,255,0.6)" }}>
                  {ad.subtitle}
                </p>
                <button
                  className="mt-2 text-[10px] px-3 py-1 rounded-full font-semibold whitespace-nowrap cursor-pointer"
                  style={{ background: ad.accent, color: "#fff" }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleAdClick(ad);
                  }}
                >
                  {ad.cta} →
                </button>
              </div>
            </div>
          ))}

          {/* dots indicator */}
          <div className="absolute bottom-2.5 right-3 flex items-center gap-1 z-20">
            {ads.map((_, i) => (
              <button
                key={i}
                onClick={() => setAdIndex(i)}
                className="rounded-full cursor-pointer transition-all duration-300"
                style={{
                  width: i === adIndex ? 14 : 5,
                  height: 5,
                  background: i === adIndex ? "#fff" : "rgba(255,255,255,0.35)",
                }}
              />
            ))}
          </div>
        </div>
      </div>
      )}

      {/* ── 优质技师推荐 ── */}
      <div className="mx-3 mt-4 mb-2">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className="w-1 h-4 rounded-full"
              style={{ background: "linear-gradient(180deg, #FF6018, #FF9A3C)" }}
            />
            <h3 className="text-sm font-bold" style={{ color: "#1A1A1A" }}>
              优质技师推荐
            </h3>
          </div>

        </div>

        {/* served therapists */}
        {servedTherapists.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2.5">
              <div
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                style={{ background: "#FFF4EC", border: "1px solid #FFD4A0" }}
              >
                <i className="ri-history-line text-[10px]" style={{ color: "#FF6018" }} />
                <span className="text-[10px] font-semibold" style={{ color: "#FF6018" }}>
                  服务过我的
                </span>
              </div>
              <div className="flex-1 h-px" style={{ background: "#EDE8E0" }} />
            </div>
            <div className="space-y-2.5">
              {servedTherapists.map((t) => (
                <TherapistCard
                  key={t.id}
                  therapist={t}
                  served
                  onBook={() => navigate(`/therapist-detail?id=${t.id}&showService=true`)}
                  onDetail={() => navigate(`/therapist/${t.id}`)}
                />
              ))}
            </div>
          </div>
        )}

        {/* recommended */}
        <div>
          {servedTherapists.length > 0 && (
            <div className="flex items-center gap-2 mb-2.5">
              <div
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                style={{ background: "#F5F3EF", border: "1px solid #E8E0D5" }}
              >
                <i className="ri-sparkling-2-line text-[10px]" style={{ color: "#999" }} />
                <span className="text-[10px] font-semibold" style={{ color: "#888" }}>
                  为你推荐
                </span>
              </div>
              <div className="flex-1 h-px" style={{ background: "#EDE8E0" }} />
            </div>
          )}
          <div className="space-y-2.5">
            {recommendedTherapists.map((t) => (
              <TherapistCard
                key={t.id}
                therapist={t}
                served={false}
                onBook={() => navigate(`/therapist-detail?id=${t.id}&showService=true`)}
                onDetail={() => navigate(`/therapist/${t.id}`)}
              />
            ))}
          </div>
        </div>
      </div>

      <BottomNav />

      {/* ─────── Sheets ─────── */}

      {/* Coupon sheet */}
      {couponsOpen && (
        <BottomSheet title="我的优惠券" onClose={() => setCouponsOpen(false)}>
          <div className="space-y-3">
            {profileCoupons.map((c) => (
              <div
                key={c.id}
                className="flex items-stretch rounded-2xl overflow-hidden"
                style={{ border: "1px solid #FFD4A0" }}
              >
                <div
                  className="flex flex-col items-center justify-center px-4 py-5 flex-shrink-0"
                  style={{
                    background: "linear-gradient(135deg,#FF5C00,#FF9A3C)",
                    minWidth: 76,
                  }}
                >
                  <span className="text-2xl font-black text-white leading-none">{c.amount}</span>
                  <span className="text-[10px] text-white opacity-80 mt-0.5">{c.unit}</span>
                </div>
                <div className="flex-1 px-4 py-3.5">
                  <p className="text-sm font-semibold" style={{ color: "#1A1A1A" }}>
                    {c.title}
                  </p>
                  <p className="text-[11px] mt-0.5" style={{ color: "#C0A882" }}>
                    {c.condition}
                  </p>
                  <p className="text-[10px] mt-1" style={{ color: "#D8C8B8" }}>
                    有效期至 {c.expiry}
                  </p>
                </div>
                <div className="flex items-center px-4">
                  <button
                    onClick={() => { setCouponsOpen(false); navigate("/"); }}
                    className="text-xs px-3.5 py-2 rounded-full font-semibold cursor-pointer whitespace-nowrap text-white"
                    style={{ background: "linear-gradient(90deg,#FF5C00,#FF9A3C)" }}
                  >
                    去使用
                  </button>
                </div>
              </div>
            ))}
            {profileCoupons.length === 0 && (
              <div className="py-10 flex flex-col items-center">
                <i className="ri-coupon-3-line text-4xl mb-3" style={{ color: "#DDD0C0" }} />
                <p className="text-sm" style={{ color: "#BBAAA0" }}>暂无可用优惠券</p>
              </div>
            )}
          </div>
        </BottomSheet>
      )}

      {/* Wallet sheet */}
      {walletOpen && (
        <BottomSheet title="我的钱包" onClose={() => setWalletOpen(false)}>
          <div
            className="relative overflow-hidden rounded-2xl p-5 mb-5"
            style={{ background: "linear-gradient(135deg, #FF7B22 0%, #FF4D00 100%)" }}
          >
            <div
              className="absolute -top-6 -right-6 w-24 h-24 rounded-full"
              style={{ background: "rgba(255,255,255,0.1)" }}
            />
            <p className="text-xs text-white opacity-70 mb-1">账户余额</p>
            <p className="text-3xl font-black text-white">¥{walletBalance}</p>
            <p className="text-xs mt-2 text-white opacity-55">消费明细 &gt;</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {["购买钱包卡", "申请提现", "消费明细", "转账记录"].map((action) => (
              <button
                key={action}
                className="py-3.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                style={{
                  background: "#FFF4EC",
                  color: "#FF6018",
                  border: "1px solid #FFD4A0",
                }}
              >
                {action}
              </button>
            ))}
          </div>
        </BottomSheet>
      )}

      {/* Address sheet */}
      {addressSheetOpen && (
        <BottomSheet title="地址管理" onClose={() => setAddressSheetOpen(false)}>
          <div className="space-y-3">
            {[
              {
                label: "家",
                addr: "上海市浦东新区张江高科技园区博云路2号",
                defaultAddr: true,
              },
              {
                label: "公司",
                addr: "上海市静安区南京西路1366号恒隆广场",
                defaultAddr: false,
              },
            ].map((a, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-4 rounded-2xl"
                style={{ background: "#FAFAF8", border: "1px solid #F0EAE0" }}
              >
                <div
                  className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 mt-0.5"
                  style={{ background: "#FFF4EC" }}
                >
                  <i className="ri-map-pin-2-line text-base" style={{ color: "#FF6018" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-sm font-semibold" style={{ color: "#1A1A1A" }}>
                      {a.label}
                    </span>
                    {a.defaultAddr && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full"
                        style={{ background: "#FFF0E6", color: "#FF6018" }}
                      >
                        默认
                      </span>
                    )}
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: "#999" }}>
                    {a.addr}
                  </p>
                </div>
                <button className="w-7 h-7 flex items-center justify-center cursor-pointer flex-shrink-0">
                  <i className="ri-edit-line text-sm" style={{ color: "#C0B8AE" }} />
                </button>
              </div>
            ))}
            <button
              className="w-full py-3.5 rounded-2xl text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
              style={{
                border: "1.5px dashed #FFD4A0",
                color: "#FF6018",
                background: "#FFFAF6",
              }}
            >
              <i className="ri-add-line text-base" />
              添加新地址
            </button>
          </div>
        </BottomSheet>
      )}

      {/* Feedback sheet */}
      {feedbackOpen && (
        <BottomSheet
          title="建议反馈"
          onClose={() => {
            setFeedbackOpen(false);
            setFeedbackSubmitted(false);
            setFeedbackContent("");
            setFeedbackEmail("");
            setFeedbackError("");
          }}
        >
          {feedbackSubmitted ? (
            <div className="py-10 flex flex-col items-center">
              <div
                className="w-16 h-16 flex items-center justify-center rounded-full mb-4"
                style={{ background: "#FFF4EC" }}
              >
                <i className="ri-check-double-line text-3xl" style={{ color: "#FF6018" }} />
              </div>
              <p className="text-base font-bold mb-1.5" style={{ color: "#1A1A1A" }}>
                感谢你的反馈！
              </p>
              <p className="text-sm text-center leading-relaxed" style={{ color: "#999" }}>
                我们会认真对待每一条建议
                <br />
                努力让摩康变得更好
              </p>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleFeedbackSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold mb-2 block" style={{ color: "#888" }}>
                  反馈类型
                </label>
                <div className="flex flex-wrap gap-2">
                  {["功能建议", "体验问题", "技师服务", "支付问题", "其他"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFeedbackType(type)}
                      className="text-xs px-3 py-1.5 rounded-full font-medium cursor-pointer whitespace-nowrap transition-all"
                      style={{
                        background: feedbackType === type ? "#FF5C00" : "#F5F2EE",
                        color: feedbackType === type ? "#fff" : "#888",
                        border: feedbackType === type ? "none" : "1px solid #E8E0D8",
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold mb-1.5 block" style={{ color: "#888" }}>
                  内容 <span style={{ color: "#FF5C00" }}>*</span>
                </label>
                <textarea
                  name="content"
                  value={feedbackContent}
                  onChange={(e) => setFeedbackContent(e.target.value.slice(0, 500))}
                  placeholder="说说你的想法，越详细越好…"
                  className="w-full rounded-xl p-3 text-sm resize-none outline-none"
                  rows={4}
                  style={{
                    background: "#FAFAF8",
                    border: "1px solid #EDE8E0",
                    color: "#1A1A1A",
                  }}
                />
                <p
                  className="text-right text-[10px] mt-0.5"
                  style={{ color: feedbackContent.length > 480 ? "#FF5C00" : "#C0B8AE" }}
                >
                  {feedbackContent.length}/500
                </p>
              </div>
              <div>
                <label className="text-xs font-semibold mb-1.5 block" style={{ color: "#888" }}>
                  联系邮箱（可选）
                </label>
                <input
                  type="email"
                  name="email"
                  value={feedbackEmail}
                  onChange={(e) => setFeedbackEmail(e.target.value)}
                  placeholder="方便我们回复你的建议"
                  className="w-full rounded-xl p-3 text-sm outline-none"
                  style={{
                    background: "#FAFAF8",
                    border: "1px solid #EDE8E0",
                    color: "#1A1A1A",
                  }}
                />
              </div>
              {feedbackError && (
                <p className="text-xs" style={{ color: "#FF5C00" }}>
                  {feedbackError}
                </p>
              )}
              <button
                type="submit"
                className="w-full py-3.5 rounded-2xl text-sm font-bold cursor-pointer whitespace-nowrap text-white"
                style={{ background: "linear-gradient(90deg,#FF5C00,#FF9A3C)" }}
              >
                提交反馈
              </button>
            </form>
          )}
        </BottomSheet>
      )}
    </div>
  );
}

/* ── Bottom Sheet ── */
function BottomSheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ maxWidth: 480, margin: "0 auto", left: "50%", transform: "translateX(-50%)" }}
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative w-full bg-white rounded-t-3xl pb-10"
        style={{ zIndex: 51 }}
      >
        <div
          className="w-10 h-1 rounded-full mx-auto mt-3 mb-4"
          style={{ background: "#E0D8D0" }}
        />
        <div className="px-5 mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold" style={{ color: "#1A1A1A" }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center cursor-pointer"
          >
            <i className="ri-close-line text-lg" style={{ color: "#999" }} />
          </button>
        </div>
        <div className="px-5">{children}</div>
      </div>
    </div>
  );
}

function TherapistCard({
  therapist: t,
  served,
  onBook,
  onDetail,
}: {
  therapist: Therapist;
  served: boolean;
  onBook: () => void;
  onDetail: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onDetail}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onDetail();
        }
      }}
      className="w-full flex items-center gap-3 p-3.5 rounded-2xl cursor-pointer text-left active:opacity-80"
      style={{
        background: "#fff",
        border: `1px solid ${served ? "#FFD4A0" : "#F0EAE0"}`,
      }}
    >
      {/* avatar */}
      <div className="relative flex-shrink-0">
        <div
          className="w-[52px] h-[52px] rounded-full overflow-hidden"
          style={{
            border: served ? "2.5px solid #FF7B22" : "2px solid #EDE8E0",
          }}
        >
          <img
            src={t.avatar}
            alt={t.name}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <span
          className="absolute bottom-0 right-0 w-3 h-3 rounded-full"
          style={{
            background:
              t.status === "可服务"
                ? "#17B978"
                : t.status === "服务中"
                ? "#FF7B22"
                : "#C0C0C0",
            border: "1.5px solid #fff",
          }}
        />
      </div>

      {/* info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-sm font-bold truncate" style={{ color: "#1A1A1A" }}>
            {t.name}
          </span>
          <span
            className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0"
            style={
              t.levelStyle
                ? { background: t.levelStyle.bg, color: t.levelStyle.color }
                : {}
            }
          >
            {t.level}
          </span>
        </div>
        <div className="flex items-center gap-1 mb-1.5">
          <i className="ri-star-fill text-[11px]" style={{ color: "#FF7B22" }} />
          <span className="text-xs font-semibold" style={{ color: "#FF7B22" }}>
            {t.rating}
          </span>
          <span className="text-[11px]" style={{ color: "#C0B0A0" }}>
            · {t.orders}单
          </span>
        </div>
        <div className="flex flex-wrap gap-1">
          {t.specialties.slice(0, 2).map((s) => (
            <span
              key={s}
              className="text-[10px] px-1.5 py-0.5 rounded-full"
              style={{ background: "#FFF4EC", color: "#FF6018" }}
            >
              {s}
            </span>
          ))}
        </div>
      </div>

      {/* action */}
      <div className="flex-shrink-0 flex flex-col items-end gap-2">
        {served ? (
          <button
            onClick={(e) => { e.stopPropagation(); onBook(); }}
            className="text-xs px-3 py-1.5 rounded-full font-semibold cursor-pointer whitespace-nowrap text-white"
            style={{ background: "linear-gradient(90deg,#FF5C00,#FF9A3C)" }}
          >
            再次预约
          </button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onDetail(); }}
            className="text-xs px-3 py-1.5 rounded-full font-semibold cursor-pointer whitespace-nowrap"
            style={{
              background: "#FFF4EC",
              color: "#FF6018",
              border: "1px solid #FFD4A0",
            }}
          >
            查看详情
          </button>
        )}
        <span
          className="text-[10px] leading-none"
          style={{ color: t.status === "可服务" ? "#17B978" : "#C0B0A0" }}
        >
          {t.status}
        </span>
      </div>
    </div>
  );
}
